import { useCanvasById } from "@/api/canvas";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ROUTES } from "@/constants/routes";
import type { CanvasTab, ChatTab, OpenTab } from "@/context/OpenTabsContext";
import { useOpenTabs } from "@/context/OpenTabsContext";
import { markTabBarContainerNavigation } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/railDrawerNavigationSource";
import { route } from "@/utils/route";
import {
  type Canvas,
  FLOW_SESSION_MODES,
  type InboxItem,
} from "@cohesive/api-client";
import { CanvasCard, ChatCard } from "@cohesive/ui-components";
import { X } from "lucide-react";
import React, { useCallback, useMemo, useState } from "react";
import { useMatch, useNavigate } from "react-router-dom";

function tabRoute(tab: OpenTab): string {
  if (tab.kind === "chat") {
    return route(ROUTES.CHAT.DETAIL, { flowSessionId: tab.id });
  }
  return route(ROUTES.CANVAS.DETAIL, { canvasId: tab.id });
}

function openTabToCanvasBase(tab: CanvasTab): Canvas {
  const now = new Date().toISOString();
  return {
    id: tab.id,
    organization_id: "",
    user_id: "",
    liveblocks_room_id: "",
    name: tab.name,
    created_at: now,
    updated_at: now,
  };
}

/** Canvas stub for `CanvasCard`, with theme-aware hero fields (see `WorkspaceTabStrip`). */
function useOpenCanvasTabCardCanvas(tab: CanvasTab): Canvas {
  // Hero URLs come from the ["canvas", id] query only — presigned URLs are
  // never stored on the tab record (see CanvasTab).
  const { data: freshCanvas } = useCanvasById(tab.id, {
    staleTime: 30 * 60 * 1000,
    refetchOnWindowFocus: true,
  });

  return useMemo(() => {
    const base = openTabToCanvasBase(tab);
    if (freshCanvas) {
      return {
        ...base,
        hero_image_url: freshCanvas.hero_image_url,
        hero_image_light_url: freshCanvas.hero_image_light_url,
        hero_image_dark_url: freshCanvas.hero_image_dark_url,
        updated_at: freshCanvas.updated_at,
      };
    }
    return base;
  }, [tab, freshCanvas]);
}

function openTabToInboxItem(tab: ChatTab): InboxItem {
  return {
    id: tab.id,
    organization_id: "",
    user_id: "",
    flow_session_id: tab.id,
    resource_type: "flow_session",
    is_read: true,
    is_starred: false,
    is_archived: false,
    has_pending_tool_confirmations: false,
    created_at: "",
    updated_at: "",
    flow_session: {
      id: tab.id,
      mode: FLOW_SESSION_MODES.WORKSPACE_CHAT,
      name: tab.name,
      status: "ready",
      created_at: "",
      updated_at: "",
    },
  };
}

interface WorkspaceMobileTabSwitcherProps {
  onCloseTab: (id: string) => void;
}

interface OpenTabCardProps {
  tab: OpenTab;
  isActive: boolean;
  onSelect: (tab: OpenTab) => void;
  onClose: (e: React.MouseEvent, id: string) => void;
}

function OpenCanvasTabCard({
  tab,
  isActive,
  onSelect,
  onClose,
}: {
  tab: CanvasTab;
  isActive: boolean;
  onSelect: (tab: OpenTab) => void;
  onClose: (e: React.MouseEvent, id: string) => void;
}) {
  const canvas = useOpenCanvasTabCardCanvas(tab);
  const handleSelect = useCallback(() => {
    onSelect(tab);
  }, [onSelect, tab]);

  return (
    <>
      <CanvasCard
        canvas={canvas}
        onClick={handleSelect}
        isSelected={isActive}
        className="[&>div:last-child>div:first-child>div:last-child]:hidden"
      />
      <button
        type="button"
        aria-label={`Close ${tab.name}`}
        onClick={(e) => onClose(e, tab.id)}
        className="absolute right-2 top-2 z-10 inline-flex size-7 items-center justify-center rounded-full bg-background/70 text-foreground/70 backdrop-blur-sm transition-colors hover:bg-background hover:text-foreground"
      >
        <X className="size-3.5" />
      </button>
    </>
  );
}

function OpenTabCard({ tab, isActive, onSelect, onClose }: OpenTabCardProps) {
  const handleSelect = useCallback(() => {
    onSelect(tab);
  }, [onSelect, tab]);

  return (
    <div className="relative">
      {tab.kind === "canvas" ? (
        <OpenCanvasTabCard
          tab={tab}
          isActive={isActive}
          onSelect={onSelect}
          onClose={onClose}
        />
      ) : (
        <>
          <ChatCard
            item={openTabToInboxItem(tab)}
            onClick={handleSelect}
            isSelected={isActive}
          />
          <button
            type="button"
            aria-label={`Close ${tab.name}`}
            onClick={(e) => onClose(e, tab.id)}
            className="absolute right-2 top-2 z-10 inline-flex size-7 items-center justify-center rounded-full bg-background/70 text-foreground/70 backdrop-blur-sm transition-colors hover:bg-background hover:text-foreground"
          >
            <X className="size-3.5" />
          </button>
        </>
      )}
    </div>
  );
}

export function WorkspaceMobileTabSwitcher({
  onCloseTab,
}: WorkspaceMobileTabSwitcherProps) {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { openTabs } = useOpenTabs();
  const isCanvasDetail = useMatch(ROUTES.CANVAS.DETAIL);
  const isChatDetail = useMatch(ROUTES.CHAT.DETAIL);
  const activeId =
    isCanvasDetail?.params?.canvasId ??
    isChatDetail?.params?.flowSessionId ??
    null;

  const handleSelect = useCallback(
    (tab: OpenTab) => {
      // Tab-bar navigations skip the drawer's rail-collision cap — there is
      // no rail selection to keep visible when switching through tabs. Only
      // an actual switch marks; re-tapping the open tab changes no route.
      if (tab.id !== activeId) markTabBarContainerNavigation();
      navigate(tabRoute(tab));
      setOpen(false);
    },
    [navigate, activeId],
  );

  const handleClose = useCallback(
    (e: React.MouseEvent, id: string) => {
      e.stopPropagation();
      onCloseTab(id);
    },
    [onCloseTab],
  );

  // A pending tab has no canvas to card up — it is a strip-only placeholder.
  const switchableTabs = openTabs.filter(
    (t) => !(t.kind === "canvas" && t.pending),
  );

  if (switchableTabs.length === 0) {
    return null;
  }

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label={`${switchableTabs.length} open tabs`}
        className="inline-flex size-8 shrink-0 flex-col items-center justify-center rounded-md border border-n-10 bg-n-00 text-n-70 outline-none transition-colors hover:bg-n-05 focus-visible:outline-2 focus-visible:outline-rust focus-visible:outline-offset-[-2px]"
      >
        <span className="font-mono text-2xs font-medium leading-none">
          {switchableTabs.length}
        </span>
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[85dvh] overflow-hidden sm:max-w-md">
          <DialogHeader className="space-y-0">
            <DialogTitle className="text-base">Open tabs</DialogTitle>
          </DialogHeader>
          <div className="grid max-h-[60dvh] grid-cols-2 gap-x-3 gap-y-6 overflow-y-auto py-1">
            {switchableTabs.map((tab) => (
              <OpenTabCard
                key={tab.id}
                tab={tab}
                isActive={tab.id === activeId}
                onSelect={handleSelect}
                onClose={handleClose}
              />
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
